import React, { ReactNode } from 'react';

import { FormattedMessage, IntlShape } from 'react-intl';

import { BaseStyle, ReactTableEnums, CoreCommonMessages, CoreUtils, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import { ColumnDef, ColumnHelper, createColumnHelper, DisplayColumnDef } from '@tanstack/react-table';

import commonMessages from '~/core/commonMessages';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { CommonGCGame, LineScore, PeriodValue } from '../../types';

import { TeamGoals, TeamShootout } from './LineScore';

import { FinalContainer, ShootoutColumn, TeamAbbrev, TeamInfoContainer, TeamLogo } from './styles';

const periodColumnWidth = 40;
const periodColumnMaxWidth = 50;
const columnHelper: ColumnHelper<TeamGoals> = createColumnHelper<TeamGoals>();

const FINAL_COLUMN = (intl: IntlShape): DisplayColumnDef<TeamGoals> =>
    columnHelper.display({
        id: 'final',
        size: 75,
        header: '',
        cell: ({ row }) => (
            <FinalContainer aria-label={parseValueFromLocalizedAttribute(row.original.name)}>
                <TeamLogo
                    logoUrlOverride={row.original.logo}
                    team={{ triCode: row.original.abbrev }}
                    displayName={false}
                />
                <TeamInfoContainer>
                    <TeamAbbrev>{row.original.abbrev}</TeamAbbrev>
                    {/*
                        TODO: Record is not in the API right now
                        <Record></Record>
                    */}
                </TeamInfoContainer>
            </FinalContainer>
        ),
        meta: {
            className: 'left-aligned',
            headerAriaLabel: () => intl.formatMessage(commonMessages.tableHeaders.long.team),
            headerClassName: 'left-aligned',
            style: 'width: fit-content',
            scope: ReactTableEnums.Scopes.ROW,
        },
    });

const createPeriodColumn = (period: number, header: ReactNode, width = periodColumnWidth): ColumnDef<TeamGoals> =>
    columnHelper.display({
        id: period + '',
        size: width,
        maxSize: periodColumnMaxWidth,
        header: () => header,
        cell: (data) =>
            data.row.original[period] === undefined ? (
                <FormattedMessage {...commonMessages.statsDescriptions.symbols.noInfo} />
            ) : (
                data.row.original[period]
            ),
        meta: {
            headerStyle: 'text-align: center',
            style: 'text-align: center',
        },
    });

const createShootoutColumn = (header: ReactNode, intl: IntlShape): ColumnDef<TeamGoals> =>
    columnHelper.display({
        id: 'shootout',
        size: periodColumnWidth,
        maxSize: periodColumnMaxWidth,
        header: () => header,
        cell: (data) => {
            const shootout = data.row.original.shootout as TeamShootout;
            const shootoutLabel = shootout.isWinner
                ? intl.formatMessage(
                      {
                          defaultMessage: 'The winning team went {conversions} for {attempts} in the shootout',
                          description: 'This describes how the winning team did in a shootout.',
                      },
                      shootout
                  )
                : intl.formatMessage(
                      {
                          defaultMessage: '{conversions} out of {attempts} in the shootout',
                          description: 'This describes how a team did in a shootout.',
                      },
                      shootout
                  );
            return (
                <span aria-label={shootoutLabel}>
                    <FormattedMessage
                        defaultMessage="{isWinner} <grayText>({conversions}/{attempts})</grayText>"
                        description="This describes the results of a shootout. EX. '2 (2/3)' 2 goals for 3 shot attempts"
                        values={{
                            grayText: (text) => <ShootoutColumn>{text}</ShootoutColumn>,
                            ...shootout,
                        }}
                    />
                </span>
            );
        },
        meta: {
            headerStyle: 'text-align: center',
            style: 'text-align: center',
            headerTitle: intl.formatMessage(
                CoreCommonMessages.gamePeriodDescriptions.long[ShortCodeEnums.PeriodType.shootout]
            ),
        },
    });

export const createPeriodColumns = (
    game: CommonGCGame,
    linescore: LineScore,
    intl: IntlShape
): Array<ColumnDef<TeamGoals>> => {
    const periodScoring: Array<PeriodValue> = linescore.byPeriod;
    const periodColumns: Array<ColumnDef<TeamGoals>> = [FINAL_COLUMN(intl)];

    // first, iterate over and insert all game Regulation Periods
    const regulationPeriods: Array<number> = Array(game.regPeriods)
        .fill(0)
        .map((x, i) => i + 1);

    regulationPeriods.forEach((periodNumber) => {
        const regulationPeriodDescriptor = {
            number: periodNumber,
            periodType: ShortCodeEnums.PeriodType.regulation,
            maxRegulationPeriods: game.regPeriods,
        };
        const regulationPeriodHeader = CoreUtils.getPeriodDisplayFromDescriptor(
            intl,
            regulationPeriodDescriptor,
            game.gameType
        );
        periodColumns.push(createPeriodColumn(periodNumber, regulationPeriodHeader));
    });

    // next, insert final Overtime | Shootout Period (if game went to OT)
    if (periodScoring.length > game.regPeriods) {
        const overtimePeriod = periodScoring[periodScoring.length - 1];
        const message = CoreUtils.getPeriodDisplayFromDescriptor(intl, overtimePeriod.periodDescriptor, game.gameType);
        if (overtimePeriod.periodDescriptor.periodType === ShortCodeEnums.PeriodType.shootout) {
            periodColumns.push(createShootoutColumn(message, intl));
        } else {
            periodColumns.push(createPeriodColumn(overtimePeriod.periodDescriptor.number, message, periodColumnWidth));
        }
    }

    // finally, insert scores Total column for all periods
    periodColumns.push(
        columnHelper.display({
            id: 'total',
            size: periodColumnWidth,
            maxSize: periodColumnMaxWidth,
            header: () => (
                <FormattedMessage description="Abbreviation for the total number of goals" defaultMessage="T" />
            ),
            cell: (data) => data.row.original.total,
            meta: {
                headerStyle: 'text-align: center',
                headerTitle: intl.formatMessage(commonMessages.statsDescriptions.long.total),
                style: `text-align: center; ${BaseStyle.Mixins.fontStyles.ui['14b']}`,
            },
        })
    );
    return periodColumns;
};

export default [];
